
import { useRef, useCallback } from 'react';

export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const getViewportHeight = (): number => {
  // Prefer visual viewport for better mobile support
  if (window.visualViewport) {
    return window.visualViewport.height;
  }
  return window.innerHeight;
};

export const getViewportWidth = (): number => {
  // Prefer visual viewport for better mobile support
  if (window.visualViewport) {
    return window.visualViewport.width;
  }
  return window.innerWidth;
};

// Enhanced mobile detection with better device support
export const isMobileOS = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(navigator.userAgent);
};

// Mobile-specific viewport utilities
export const getMobileViewportInfo = () => {
  const isActualMobile = isMobileOS();
  const visualViewport = window.visualViewport;
  
  return {
    isActualMobile,
    hasVisualViewport: !!visualViewport,
    viewportHeight: visualViewport?.height || window.innerHeight,
    windowHeight: window.innerHeight,
    safeAreaBottom: window.CSS?.supports('padding-bottom', 'env(safe-area-inset-bottom)'),
    userAgent: navigator.userAgent
  };
};

export const isLandscape = (): boolean => {
  return window.innerWidth > window.innerHeight;
};

export const isPortrait = (): boolean => {
  return window.innerHeight > window.innerWidth;
};

export const preventZoom = (element: HTMLElement): void => {
  element.addEventListener('touchstart', (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  });
};

// Enhanced haptic feedback with fallbacks
export const hapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light'): void => {
  // Standard vibration API for cross-platform support
  if ('vibrate' in navigator) {
    const patterns = {
      light: [5],
      medium: [10],
      heavy: [15]
    };
    navigator.vibrate(patterns[type]);
  }
};

export const scrollToTop = (smooth: boolean = true): void => {
  window.scrollTo({
    top: 0,
    behavior: smooth ? 'smooth' : 'auto'
  });
};

// Enhanced scroll lock with cleanup tracking
let scrollLockCount = 0;
let originalBodyStyles: { overflow: string; position: string; width: string; top: string } | null = null;

export const lockScrolling = (): void => {
  scrollLockCount++;
  
  if (scrollLockCount === 1) {
    // Store original styles only on first lock
    originalBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top
    };
    
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Apply lock styles
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
  }
};

export const unlockScrolling = (): void => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  
  if (scrollLockCount === 0 && originalBodyStyles) {
    // Get the scroll position from the fixed top value
    const scrollY = parseInt(document.body.style.top || '0') * -1;
    
    // Restore original styles
    document.body.style.overflow = originalBodyStyles.overflow;
    document.body.style.position = originalBodyStyles.position;
    document.body.style.width = originalBodyStyles.width;
    document.body.style.top = originalBodyStyles.top;
    
    // Restore scroll position
    window.scrollTo(0, scrollY);
    
    // Clear stored styles
    originalBodyStyles = null;
  }
};

// Force cleanup for emergency situations
export const forceUnlockScrolling = (): void => {
  scrollLockCount = 0;
  
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  
  originalBodyStyles = null;
};

// Advanced overscroll prevention
export const preventOverscroll = (element: HTMLElement, options: {
  horizontal?: boolean;
  vertical?: boolean;
} = {}): (() => void) => {
  const { horizontal = true, vertical = true } = options;
  
  // CSS-based prevention
  if (horizontal && vertical) {
    element.style.overscrollBehavior = 'none';
    element.style.touchAction = 'manipulation';
  } else if (horizontal) {
    element.style.overscrollBehaviorX = 'none';
    element.style.touchAction = 'pan-y manipulation';
  } else if (vertical) {
    element.style.overscrollBehaviorY = 'none';
    element.style.touchAction = 'pan-x manipulation';
  }
  
  // Programmatic prevention for older browsers
  let lastTouchY = 0;
  let lastTouchX = 0;
  
  const handleTouchStart = (e: TouchEvent) => {
    lastTouchY = e.touches[0].clientY;
    lastTouchX = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e: TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const diffY = currentY - lastTouchY;
    const diffX = currentX - lastTouchX;
    
    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = element;
    
    // Prevent vertical overscroll
    if (vertical && (
      (scrollTop === 0 && diffY > 0) || 
      (scrollTop + clientHeight >= scrollHeight && diffY < 0)
    )) {
      e.preventDefault();
    }
    
    // Prevent horizontal overscroll
    if (horizontal && (
      (scrollLeft === 0 && diffX > 0) || 
      (scrollLeft + clientWidth >= scrollWidth && diffX < 0)
    )) {
      e.preventDefault();
    }
  };
  
  element.addEventListener('touchstart', handleTouchStart, { passive: false });
  element.addEventListener('touchmove', handleTouchMove, { passive: false });
  
  // Return cleanup function
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchmove', handleTouchMove);
    element.style.overscrollBehavior = '';
    element.style.touchAction = '';
  };
};

// Enhanced gesture detection with priority system
export interface GestureConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  minSwipeDistance?: number;
  maxSwipeTime?: number;
  lockDirection?: boolean;
  priority?: 'low' | 'medium' | 'high';
}

export const useEnhancedGestures = (config: GestureConfig) => {
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const directionLockRef = useRef<'horizontal' | 'vertical' | null>(null);
  
  const {
    minSwipeDistance = 50,
    maxSwipeTime = 300,
    lockDirection = true,
    priority = 'medium'
  } = config;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    directionLockRef.current = null;
    
    // Add haptic feedback for gesture start
    hapticFeedback('light');
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current || !lockDirection) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // Establish direction lock
    if (!directionLockRef.current && (deltaX > 10 || deltaY > 10)) {
      directionLockRef.current = deltaX > deltaY ? 'horizontal' : 'vertical';
    }
    
    // Prevent scroll in locked direction
    if (directionLockRef.current === 'horizontal') {
      e.preventDefault();
    }
  }, [lockDirection]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;

    // Check timing constraint
    if (deltaTime > maxSwipeTime) return;

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Check distance constraint
    if (absX < minSwipeDistance && absY < minSwipeDistance) return;

    // Determine gesture direction and execute callback
    if (absX > absY) {
      // Horizontal swipe
      if (deltaX > 0) {
        config.onSwipeRight?.();
        hapticFeedback('medium');
      } else {
        config.onSwipeLeft?.();
        hapticFeedback('medium');
      }
    } else {
      // Vertical swipe
      if (deltaY > 0) {
        config.onSwipeDown?.();
        hapticFeedback('medium');
      } else {
        config.onSwipeUp?.();
        hapticFeedback('medium');
      }
    }

    touchStartRef.current = null;
    directionLockRef.current = null;
  }, [config, minSwipeDistance, maxSwipeTime]);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
